/**
 * Linking an outbound call log to the customer it was made for.
 *
 * The sidecar creates the OUTBOUND CallLog when the call is placed, with no customer on it. The
 * portal finds that row by the dialled number and attaches the customer, because the portal is
 * the only side that knows which profile the operator dialled from.
 */

/** How far a server start time may sit before the moment the portal dialled, for clock drift. */
const DIAL_CLOCK_SKEW_MS = 15_000;

/** Context passed with an outbound dial so its call log can be linked. */
export type OutboundDialOptions = {
  /** The customer whose profile the call was placed from. */
  customerId?: string | null;
};

export type OutboundCallLogCandidate = {
  asteriskId?: string | null;
  callUuid?: string | null;
  callerNumber?: string | null;
  caller?: string | null;
  direction?: string | null;
  startedAt?: string | null;
  customerId?: string | null;
};

export function isOutboundDirection(value: unknown): boolean {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase();
  return (
    normalized === "outbound" ||
    normalized === "outgoing" ||
    normalized === "out"
  );
}

/**
 * The customer an outbound call should be linked to, if any.
 *
 * A customer passed with the dial is authoritative: the operator pressed call on that customer's
 * profile. Without one, the customer already held in call state is only trusted while the
 * operator is dialling out from a call they are on, such as ringing an emergency contact with the
 * client on hold. Once a call ends that customer stays in state, and relying on it then linked
 * the next call to the previous client.
 */
export function resolveOutboundLinkCustomerId(input: {
  explicitCustomerId?: string | null;
  stateCustomerId?: string | null;
  dialingFromActiveCall: boolean;
}): string | null {
  const explicit = input.explicitCustomerId?.trim();
  if (explicit) return explicit;

  const fromState = input.stateCustomerId?.trim();
  return input.dialingFromActiveCall && fromState ? fromState : null;
}

/**
 * The call log row for a call that was just dialled. Rows arrive newest first.
 *
 * Ended calls are accepted: a call that connects and hangs up within a couple of seconds has
 * already ended by the time its row is found, and skipping ended rows left short calls unlinked.
 * Rows that started before the dial are rejected instead, so an earlier call to the same number
 * is never mistaken for this one.
 */
export function pickOutboundCallLogMatch(
  rows: OutboundCallLogCandidate[],
  input: {
    dialedNumber: string;
    customerId: string;
    dialedAtMs: number;
    numbersMatch: (a?: string | null, b?: string | null) => boolean;
  }
): { uniqueId: string; alreadyLinked: boolean } | null {
  for (const row of rows) {
    if (!isOutboundDirection(row.direction)) continue;
    if (!input.numbersMatch(row.callerNumber ?? row.caller ?? null, input.dialedNumber)) {
      continue;
    }

    const startedAtMs = row.startedAt ? Date.parse(row.startedAt) : Number.NaN;
    if (!Number.isFinite(startedAtMs)) continue;
    if (startedAtMs < input.dialedAtMs - DIAL_CLOCK_SKEW_MS) continue;

    if (row.customerId && row.customerId !== input.customerId) continue;

    const uniqueId = row.asteriskId?.trim() || row.callUuid?.trim();
    if (!uniqueId) continue;

    return { uniqueId, alreadyLinked: row.customerId === input.customerId };
  }

  return null;
}
